import { forwardRef, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * A dropdown panel rendered in a portal with fixed positioning, anchored under (or, when there
 * isn't room, above) `anchor`. Escapes `overflow-hidden` / scrolling containers — the reason
 * autocomplete lists in table rows used to be clipped (CIPP mapping page, bottom rows).
 *
 * Callers doing outside-click detection must treat clicks inside this panel as "inside": pass a
 * ref and check it alongside their own container.
 *
 * Inside a Radix dialog it renders inline (absolute, below the anchor) instead: a body portal would
 * sit under the dialog's pointer-events lock and count as an "outside" click that closes it, and the
 * dialog's transform would break fixed positioning. Dialogs don't clip, so inline is fine there.
 * (The anchor's parent must be `relative` for that case — CompanySearch / SearchSelect are.)
 */
export const FloatingPanel = forwardRef<HTMLDivElement, {
  anchor: HTMLElement | null;
  children: React.ReactNode;
  className?: string;
  /** Preferred max height in px; shrinks to the space available. */
  maxHeight?: number;
  minWidth?: number;
}>(function FloatingPanel({ anchor, children, className, maxHeight = 288, minWidth = 220 }, ref) {
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor) return;
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const gap = 4;
      const below = window.innerHeight - r.bottom - gap - 8;
      const above = r.top - gap - 8;
      const width = Math.max(r.width, minWidth);
      const left = Math.min(r.left, window.innerWidth - width - 8);
      // Open upwards when the list wouldn't fit below and there's more room above.
      if (below < Math.min(maxHeight, 160) && above > below) {
        setPos({ bottom: window.innerHeight - r.top + gap, left, width, maxHeight: Math.min(maxHeight, above) });
      } else {
        setPos({ top: r.bottom + gap, left, width, maxHeight: Math.min(maxHeight, Math.max(below, 120)) });
      }
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true); // capture: any scrolling ancestor
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, maxHeight, minWidth]);

  const inDialog = !!anchor?.closest('[role="dialog"]');
  if (anchor && inDialog) {
    return (
      <div ref={ref} style={{ maxHeight, minWidth }}
        className={cn('absolute left-0 top-full z-50 mt-1 w-full overflow-y-auto rounded-md border border-border bg-popover py-1 text-popover-foreground shadow-lg', className)}>
        {children}
      </div>
    );
  }
  if (!anchor || !pos) return null;
  return createPortal(
    <div
      ref={ref}
      style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, pointerEvents: 'auto' }}
      className={cn('z-[100] overflow-y-auto rounded-md border border-border bg-popover py-1 text-popover-foreground shadow-lg', className)}
    >
      {children}
    </div>,
    document.body,
  );
});
